function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
// @ts-strict-ignore
/**
 * A query-builder "block" wires together the following query components:
 * - header (e.g. "USERS THAT <DID>")
 * - search-bar input (e.g. "Select event")
 * - dropdown (e.g. event selector, time picker)
 */
import isEqual from 'lodash/isEqual';
import React, { useState, useRef, useMemo, useEffect, useImperativeHandle } from 'react';
import clsx from 'clsx';
import { emitTrackEvent, rafRetry } from 'common/util/dom';
import { ButtonSize, ButtonTheme as ButtonThemes } from 'common/components/button/constants';
import { addToast } from 'common/components/toast-area/toast-manager';
import { ToastType } from 'common/components/toast/types';
import { AUTOMATED_CUSTOM_PROPERTY_TRACKING } from 'common/constants';
import { cohortsStore } from 'common/report/stores/cohorts';
import { customPropertiesStore } from 'common/report/stores/custom-properties';
import { eventPropertyDefinitionsStore, groupPropertyDefinitionsStore, peoplePropertyDefinitionsStore } from 'common/report/stores/property-definitions';
import { DisplayContext as FilterMenuDisplayContext } from 'common/widgets/property-filter-menu/models/types';
import { EntityTypeNameSingular } from 'common/report/util/saved-entities/types';
import { SelectAllMechanism } from 'common/components/items-menu/types';
import { Directions } from 'common/components/drop-menu/types';
import { PositioningStrategy } from 'common/components/tooltip/types';
import { getProjectId, getWorkspaceId } from 'common/report/util';
import { PropertyFilter } from 'common/widgets/property-filter-menu/models/property-filter';
import { SvgIconType } from 'common/components/svg-icon';
import { ContextPanelReact, ContextPanelReactProps } from 'common/widgets/context-panel';
import { useMetrics } from 'common/contexts/react/metrics-resource-context';
import { useBehaviors } from 'common/contexts/react/behaviors-context';
import { useDomEvent } from 'common/hooks/useDomEvent';
import { useEventsController } from 'common/contexts/react/events-controller-context';
import { useTranslation } from 'common/i18n/hooks';
import { usePrevious } from 'common/hooks/usePrevious';
import { queryBuilderUtils } from '../query-builder/util';
import { SelectorType, TRACKING_EVENTS } from './constants';
import { BreakdownMenu } from './breakdown-menu';
import { EventsMenu, EventsMenuReact } from './events-menu';
import { FilterMenuReact } from './filter-menu';
import { MultiEventsMenuReact } from './multi-events-menu';
import { FormulasMenu, FormulasMenuReact } from './formulas-menu';
import { updateEntityDescription } from './utils/util';
import { MenuType, QueryBlockParams } from './types';
import { IncludeCustomPropertyType, PropertyCombineMode } from './properties-menu/types';
import { BehaviorsMenu, BehaviorsMenuReact } from './behaviors-menu';
import { ChildMenuInterface, ContextPanelRenderer } from './child-menu';
import { PropertiesMenuReact } from './properties-menu';
import { NewQueryBlock } from './new-query-block';
import { convertDataGroupIdToSet } from './utils/data-group-util';

// Static definition of all registered child menus
const MENU_TYPE_MAP: Partial<Record<MenuType, any>> = {
  [MenuType.Behaviors]: BehaviorsMenu,
  [MenuType.Events]: EventsMenu,
  [MenuType.Formulas]: FormulasMenu,
  [MenuType.SavedMetrics]: FormulasMenu
};
const QueryBlock = React.forwardRef<HTMLDivElement, QueryBlockParams>((props, ref) => {
  var _childMenu$selectedLa, _childMenu$selectedBr, _childMenu$canOpenMen, _renderContextPanelFr, _childMenu$isUnsavedC, _props$labelRenderer, _childMenu$getSelecti, _props$selectorIcon, _childMenu$selectedLe, _props$selectorRightI, _childMenu$selectedRi, _props$prefixLabel, _childMenu$getPrefixL;
  const rootEl = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => rootEl.current);
  const metrics = useMetrics();
  const behaviors = useBehaviors();
  const eventsController = useEventsController();
  const {
    print
  } = useTranslation();
  const [childMenu, setChildMenu] = useState<ChildMenuInterface>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(props.open);
  const [openedMenuForModal, setOpenedMenuForModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tooltipData, setTooltipData] = useState(null);
  const [keydownProxyEl, setKeydownProxyEl] = useState(null);
  const previousOpenProp = usePrevious(props.open);
  const onChildMenuUpdate = useChildMenuSync(childMenu);
  useEffect(() => {
    if (typeof eventsController.loading === "undefined") {
      eventsController.fetchData();
    }
  }, [eventsController]);
  useDomEvent(rootEl.current, "track", props.onTrack);
  useDomEvent(rootEl.current, "savedCustomEvent", props.onSavedCustomEvent);
  const projectId = Number(props.projectId || getProjectId());
  const workspaceId = Number(props.workspaceId || getWorkspaceId());
  const openMenu = () => {
    var _props$onOpen;
    setIsMenuOpen(true);
    (_props$onOpen = props.onOpen) === null || _props$onOpen === void 0 || _props$onOpen.call(props);
  };
  const closeMenu = () => {
    var _childMenu$handleClos, _props$onClose;
    setIsMenuOpen(false);
    childMenu === null || childMenu === void 0 || (_childMenu$handleClos = childMenu.handleCloseMenu) === null || _childMenu$handleClos === void 0 || _childMenu$handleClos.call(childMenu);
    (_props$onClose = props.onClose) === null || _props$onClose === void 0 || _props$onClose.call(props);
  };

  // update internal open state if the external one changes
  useEffect(() => {
    if (typeof props.open !== "undefined" && typeof previousOpenProp !== "undefined" && props.open !== previousOpenProp) {
      if (props.open) {
        openMenu();
      } else {
        closeMenu();
      }
    }
  });
  const selected = useMemo(() => {
    switch (props.type) {
      case MenuType.Events:
        return props.multiple ? new Set(props.selected || []) : props.selected;
      case MenuType.Filter:
        return props.selected && new PropertyFilter(props.selected) || props.selected;
      default:
        return props.selected;
    }
  }, [props.selected, props.type, props.multiple, props.displayContext]);
  const dispatchSelect = detail => {
    var _props$onSelect, _detail$commit, _childMenu$shouldClos;
    (_props$onSelect = props.onSelect) === null || _props$onSelect === void 0 || _props$onSelect.call(props, detail);
    if ((_detail$commit = detail.commit) !== null && _detail$commit !== void 0 ? _detail$commit : childMenu === null || childMenu === void 0 || (_childMenu$shouldClos = childMenu.shouldCloseOnSelect) === null || _childMenu$shouldClos === void 0 ? void 0 : _childMenu$shouldClos.call(childMenu)) {
      closeMenu();
    }
  };
  const track = function track(event, trackingProps) {
    let cancelable = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    if (!trackingProps.trackName) {
      return;
    }
    emitTrackEvent(rootEl.current, {
      eventName: event,
      props: _objectSpread(_objectSpread({}, trackingProps), {}, {
        project_id: projectId,
        tab: trackingProps.trackName
      }),
      cancelable
    });
  };
  const updateCohortDescription = detail => {
    const cohortStoreKey = detail.item.id;
    const descriptionBeforeUpdate = (cohortsStore.value[cohortStoreKey] as any).description;
    const cohortPropertyUpdate = {
      description: detail.description
    };
    const updateRequestObj = {
      projectId,
      workspaceId,
      cohortID: detail.item.id,
      cohort: cohortPropertyUpdate
    };
    const undoRequestObj = _objectSpread(_objectSpread({}, updateRequestObj), {}, {
      cohort: {
        description: descriptionBeforeUpdate
      }
    });
    updateEntityDescription({
      entityStore: cohortsStore,
      newDescription: detail.description,
      storeKey: cohortStoreKey,
      updateRequestObj,
      undoRequestObj,
      entityType: "cohort property",
      addToast,
      track
    });
  };
  const updatePropertyDescription = detail => {
    const propertyStoreKey = detail.item.name;
    let propertyStore;
    if (detail.item.dataGroupId) {
      propertyStore = groupPropertyDefinitionsStore;
    } else {
      propertyStore = detail.item.type === EntityTypeNameSingular.EventProperty ? eventPropertyDefinitionsStore : peoplePropertyDefinitionsStore;
    }
    const descriptionBeforeUpdate = propertyStore.value[propertyStoreKey].description;
    let propertyDefinition;
    if (detail.item.id) {
      propertyDefinition = {
        id: detail.item.id,
        description: detail.description,
        dataGroupId: detail.item.dataGroupId
      };
    } else {
      propertyDefinition = {
        name: detail.item.name,
        resourceType: detail.item.type === EntityTypeNameSingular.EventProperty ? "Event" : "User",
        description: detail.description,
        dataGroupId: detail.item.dataGroupId
      };
    }
    const updateRequestObj = {
      projectId,
      workspaceId,
      propertyDefinition
    };
    const undoRequestObj = _objectSpread(_objectSpread({}, updateRequestObj), {}, {
      propertyDefinition: _objectSpread(_objectSpread({}, updateRequestObj.propertyDefinition), {}, {
        description: descriptionBeforeUpdate
      })
    });
    updateEntityDescription({
      entityStore: propertyStore,
      newDescription: detail.description,
      storeKey: propertyStoreKey,
      updateRequestObj,
      undoRequestObj,
      entityType: detail.item.type === EntityTypeNameSingular.EventProperty ? "event property" : "user property",
      addToast,
      track
    });
  };
  const updateCustomPropertyDescription = async detail => {
    const customPropertyId = detail.item.id;
    const customProperty = Object.assign({}, _objectSpread(_objectSpread({}, customPropertiesStore.value[customPropertyId]), {}, {
      description: detail.description
    }));
    let extraProps = {};
    if (customProperty.pre_populated_entity_id) {
      extraProps = _objectSpread(_objectSpread({}, extraProps), {}, {
        [AUTOMATED_CUSTOM_PROPERTY_TRACKING]: customProperty.pre_populated_entity_id
      });
    }
    const oldDescription = customPropertiesStore.value[customPropertyId].description;
    const cancelable = false;
    track(TRACKING_EVENTS.EDIT_DESCRIPTION, _objectSpread({
      'New Description': detail.description,
      'old description': oldDescription,
      'access point': "context panel",
      'Entity Type': "custom property"
    }, extraProps), cancelable);
    try {
      await customPropertiesStore.put(customPropertyId, {
        projectId,
        workspaceId,
        customPropertyId,
        customProperty
      });
      const undoCallback = async () => {
        await customPropertiesStore.put(customPropertyId, {
          projectId,
          workspaceId,
          customPropertyId,
          customProperty: _objectSpread(_objectSpread({}, customProperty), {}, {
            description: oldDescription
          })
        });
      };
      addToast({
        message: "'".concat(detail.item.name, "' has been updated"),
        type: ToastType.SUCCESS,
        undoable: true,
        undoCallback
      });
    } catch (_unused) {
      addToast({
        message: "Failed to update the description",
        type: ToastType.ERROR
      });
    }
  };
  const handleChildMenuOpenedModal = () => setIsModalOpen(true);
  const handleChildMenuClosedModal = () => {
    setIsModalOpen(false);
    if (openedMenuForModal) {
      closeMenu();
    }
  };

  // Handle actions from the context tooltip attached to the query block. First, give the
  // child menu a chance to handle the action. If it's not handled by child menu, then
  // handle the action directly here.
  const handleTooltipContextAction = ev => {
    var _childMenu$handleTool;
    const {
      handled,
      openedModal
    } = (childMenu === null || childMenu === void 0 || (_childMenu$handleTool = childMenu.handleTooltipContextAction) === null || _childMenu$handleTool === void 0 ? void 0 : _childMenu$handleTool.call(childMenu, ev)) || {
      handled: false,
      openedModal: false
    };
    if (!handled) {
      throw Error("unhandled context panel action");
    }
    setTooltipData(null);

    // If a modal that lives in the child menu is open, then the child menu has to be open.
    // This can happen if you click the edit action on the context panel attached to the query block.
    // This will open the child menu's modal. However, we also have to force open the child menu,
    // otherwise the modal would be hidden.
    if (openedModal) {
      openMenu();
      setOpenedMenuForModal(true);
      setIsModalOpen(true);
    }
  };
  const handleTooltipContextEditDescription = ev => {
    const detail = ev.detail;
    switch (detail.item.type) {
      case EntityTypeNameSingular.Cohort:
        updateCohortDescription(detail);
        break;
      case EntityTypeNameSingular.EventProperty:
        updatePropertyDescription(detail);
        break;
      case EntityTypeNameSingular.UserProperty:
        updatePropertyDescription(detail);
        break;
      case EntityTypeNameSingular.CustomProperty:
        updateCustomPropertyDescription(detail);
        break;
      default:
        break;
    }
  };
  const handleSelect = ev => dispatchSelect(ev.detail);

  // todo: move child menu autofocus to itself
  useEffect(() => {
    if (isMenuOpen && keydownProxyEl) {
      // This patch is equivalent fo throwing a sleep retry since
      // However, it is a goot fix while @pablo works on a real solution
      rafRetry(() => keydownProxyEl.focus());
    }
  }, [isMenuOpen]);
  const handleDelete = (ev?: Event) => {
    var _props$onRemove;
    ev === null || ev === void 0 || ev.stopPropagation();
    (_props$onRemove = props.onRemove) === null || _props$onRemove === void 0 || _props$onRemove.call(props);
  };
  const handleShowContextPanel = () => {
    var _childMenu$getSelecte;
    const selectedHoverTooltipData = childMenu === null || childMenu === void 0 || (_childMenu$getSelecte = childMenu.getSelectedHoverTooltipData) === null || _childMenu$getSelecte === void 0 ? void 0 : _childMenu$getSelecte.call(childMenu);
    setTooltipData(selectedHoverTooltipData);
    if (selectedHoverTooltipData) {
      var _childMenu$getSelecte2;
      // Setting cancelable to false is needed to prevent mp-query-multi-block from swallowing the event
      const cancelable = false;
      track(TRACKING_EVENTS.HOVER_CONTEXT_PANEL, _objectSpread({
        'Entity name': selectedHoverTooltipData.header,
        'Query block type': props.type,
        'Has description': !!selectedHoverTooltipData.description
      }, (childMenu === null || childMenu === void 0 || (_childMenu$getSelecte2 = childMenu.getSelectedTrackingProps) === null || _childMenu$getSelecte2 === void 0 ? void 0 : _childMenu$getSelecte2.call(childMenu)) || {}), cancelable);
    }
  };

  /**
   * A given resource context panel (event, cohort or properties) needs to be defined
   * in the context of that menu type, to prevent having resource specific logic
   * in this main component
   */
  const renderContextPanelFromMenu = (panelProps: ContextPanelReactProps) => {
    var _MENU_TYPE_MAP$props$;
    const contextPanelRenderer: ContextPanelRenderer = (_MENU_TYPE_MAP$props$ = MENU_TYPE_MAP[props.type]) === null || _MENU_TYPE_MAP$props$ === void 0 ? void 0 : _MENU_TYPE_MAP$props$.contextPanelRenderer;
    return contextPanelRenderer && contextPanelRenderer({
      availableContexts: {
        metrics,
        behaviors
      },
      tooltipData,
      handleSelect: dispatchSelect,
      handleDelete,
      track,
      additionalArgs: {
        'all-data-groups': props.allDataGroups,
        'project-has-people': props.projectHasPeople,
        'track-name': props.trackName
      },
      contextPanelProps: _objectSpread(_objectSpread({}, panelProps), queryBuilderUtils.isCoreReport() ? {
        positionParent: props.contextPanelParentElement
      } : {})
    });
  };
  const contextPanelProps: Partial<ContextPanelReactProps> = {
    positionStrategy: PositioningStrategy.Fixed,
    placement: queryBuilderUtils.isCoreReport() ? Directions.Right : Directions.Down,
    offset: queryBuilderUtils.isCoreReport() ? 8 : 4,
    showArrow: true,
    elref: "contextPanel"
  };
  const selectedLabel = (childMenu === null || childMenu === void 0 || (_childMenu$selectedLa = childMenu.selectedLabel) === null || _childMenu$selectedLa === void 0 ? void 0 : _childMenu$selectedLa.call(childMenu)) || (selected === null || selected === void 0 ? void 0 : selected.label);
  return <NewQueryBlock ref={rootEl} label={!props.inputOnly && selectedLabel || props.label} autofocus={props.autofocus} browserTooltip={childMenu === null || childMenu === void 0 || (_childMenu$selectedBr = childMenu.selectedBrowserTooltipLabel) === null || _childMenu$selectedBr === void 0 ? void 0 : _childMenu$selectedBr.call(childMenu)} buttonSize={props.buttonSize} canOpenMenu={childMenu === null || childMenu === void 0 || (_childMenu$canOpenMen = childMenu.canOpenMenu) === null || _childMenu$canOpenMen === void 0 ? void 0 : _childMenu$canOpenMen.call(childMenu)} className={clsx("type-".concat(props.type), props.className)} contextPanelElement={(_renderContextPanelFr = renderContextPanelFromMenu(contextPanelProps)) !== null && _renderContextPanelFr !== void 0 ? _renderContextPanelFr : <ContextPanelReact {...contextPanelProps} data={tooltipData} positionParent={props.contextPanelParentElement} onTooltipContextAction={handleTooltipContextAction} onTooltipContextEditDescription={handleTooltipContextEditDescription} />} tooltip={props.tooltip} disabled={props.disabled} dropMenuContainerClass={"for-".concat(props.type, "-menu")} elref={props.elref} header={props.headerText || props.headerSegments} helpText={props.helpText} inputOnly={props.inputOnly} isUnsaved={childMenu === null || childMenu === void 0 || (_childMenu$isUnsavedC = childMenu.isUnsavedCustomEntity) === null || _childMenu$isUnsavedC === void 0 ? void 0 : _childMenu$isUnsavedC.call(childMenu)} labelElement={(_props$labelRenderer = props.labelRenderer) === null || _props$labelRenderer === void 0 ? void 0 : _props$labelRenderer.call(props)} labelHighlightSelections={props.highlightSelections && (childMenu === null || childMenu === void 0 || (_childMenu$getSelecti = childMenu.getSelections) === null || _childMenu$getSelecti === void 0 ? void 0 : _childMenu$getSelecti.call(childMenu))} leftIcon={((_props$selectorIcon = props.selectorIcon) !== null && _props$selectorIcon !== void 0 ? _props$selectorIcon : childMenu === null || childMenu === void 0 || (_childMenu$selectedLe = childMenu.selectedLeftIcon) === null || _childMenu$selectedLe === void 0 ? void 0 : _childMenu$selectedLe.call(childMenu)) as SvgIconType} rightIcon={((_props$selectorRightI = props.selectorRightIcon) !== null && _props$selectorRightI !== void 0 ? _props$selectorRightI : childMenu === null || childMenu === void 0 || (_childMenu$selectedRi = childMenu.selectedRightIcon) === null || _childMenu$selectedRi === void 0 ? void 0 : _childMenu$selectedRi.call(childMenu)) as SvgIconType} menuAlignment={props.menuAlignment} menuOffset={props.menuOffset} open={isMenuOpen} onChangeOpen={open => open ? openMenu() : closeMenu()} prefixLabel={(_props$prefixLabel = props.prefixLabel) !== null && _props$prefixLabel !== void 0 ? _props$prefixLabel : print(childMenu === null || childMenu === void 0 || (_childMenu$getPrefixL = childMenu.getPrefixLabel) === null || _childMenu$getPrefixL === void 0 ? void 0 : _childMenu$getPrefixL.call(childMenu))} suffixLabel={props.suffixLabel} readonly={props.readonly} preventClose={props.preventClose} preventCloseSelectors={props.type === MenuType.Properties ? [".mp-query-block-selector"] : []} removable={props.removable} selectorType={props.selectorType} theme={props.theme} tabIndex={props.tabIndex} onClick={props.onClick} onClickRemove={handleDelete} onLabelMouseEnter={() => {
    var _childMenu$handleLabe;
    return childMenu === null || childMenu === void 0 || (_childMenu$handleLabe = childMenu.handleLabelMouseEnter) === null || _childMenu$handleLabe === void 0 ? void 0 : _childMenu$handleLabe.call(childMenu);
  }} onLabelMouseLeave={() => {
    var _childMenu$handleLabe2;
    return childMenu === null || childMenu === void 0 || (_childMenu$handleLabe2 = childMenu.handleLabelMouseLeave) === null || _childMenu$handleLabe2 === void 0 ? void 0 : _childMenu$handleLabe2.call(childMenu);
  }} onSelectHeaderItem={props.onSelectHeaderItem} onShowContextPanel={handleShowContextPanel} menuElement={((_props$otherGroupByOp, _props$otherFilterOpt, _props$otherAggregati) => {
    const open = isMenuOpen && !openedMenuForModal;
    const key = props.type;
    let otherOptions;
    if ((_props$otherGroupByOp = props.otherGroupByOptions) !== null && _props$otherGroupByOp !== void 0 && _props$otherGroupByOp.length) {
      otherOptions = props.otherGroupByOptions;
    } else if ((_props$otherFilterOpt = props.otherFilterOptions) !== null && _props$otherFilterOpt !== void 0 && _props$otherFilterOpt.length) {
      otherOptions = props.otherFilterOptions;
    } else if ((_props$otherAggregati = props.otherAggregationOptions) !== null && _props$otherAggregati !== void 0 && _props$otherAggregati.length) {
      otherOptions = props.otherAggregationOptions;
    }
    const setRef = childMenuRef => {
      if (childMenuRef && childMenuRef !== childMenu) {
        setChildMenu(childMenuRef);
      }
    };
    switch (props.type) {
      case MenuType.Behaviors:
        return <BehaviorsMenuReact key={key} ref={setRef} keydownProxyEl={keydownProxyEl} onSetKeydownProxyEl={setKeydownProxyEl} selected={selected} metricType={props.metricType} open={open} eventNames={props.eventNames} projectId={props.projectId} readonly={props.readonly} trackName={props.trackName} userId={props.userId} workspaceId={props.workspaceId} onSelect={handleSelect} onCreate={props.onCreate} onRender={onChildMenuUpdate} />;
      case MenuType.Events:
      case MenuType.MultiEvents:
        {
          const EventsMenuComponent = props.type === MenuType.MultiEvents || props.multiple ? MultiEventsMenuReact : EventsMenuReact;
          return <EventsMenuComponent key={key} ref={setRef} isModalOpen={isModalOpen} onModalOpen={handleChildMenuOpenedModal} onModalClose={handleChildMenuClosedModal} keydownProxyEl={keydownProxyEl} onSetKeydownProxyEl={setKeydownProxyEl} selected={selected} tooltipData={tooltipData} onTooltipChange={setTooltipData} otherOptions={otherOptions} inverted={props.inverted} includeHidden={props.includeHidden} allDataGroups={props.allDataGroups} dataGroupIds={convertDataGroupIdToSet(props.dataGroupId, props.dataGroupIds)} metricLevelDataGroups={props.metricLevelDataGroups} excludeAutotrackEvents={props.excludeAutotrackEvents} excludeCustomEvents={props.excludeCustomEvents} hideEditControls={props.hideEditControls} multiple={props.multiple} open={open} projectHasPeople={props.projectHasPeople} readonly={props.readonly} showAllEvents={props.showAllEvents} showAnyEvent={props.showAnyEvent} showSessionEvent={props.showSessionEvent} showSpecialDatasetProperty={props.showSpecialDatasetProperty} showTopEvents={props.showTopEvents} trackName={props.trackName} eventNames={props.eventNames} projectId={props.projectId} userId={props.userId} workspaceId={props.workspaceId} onSelect={handleSelect} onDelete={handleDelete} onRender={onChildMenuUpdate} />;
        }
      case MenuType.Properties:
        return <PropertiesMenuReact key={key} ref={setRef} isModalOpen={isModalOpen} onModalOpen={handleChildMenuOpenedModal} onModalClose={handleChildMenuClosedModal} listProperty={props.listProperty} getSortedMenuSections={props.getSortedMenuSections} selected={selected} cohortMultiselect={props.cohortMultiselect} applyButtonText={props.applyButtonText} minSelection={props.minSelection} allowBehavioralPropertyCreation={props.allowBehavioralPropertyCreation} allowTypecastPillAction={props.allowTypecastPillAction} enableAllDataGroupsForScdProps={props.enableAllDataGroupsForScdProps} enableWorkspaceFilterRestrictions={props.enableWorkspaceFilterRestrictions} disabledItems={props.disabledItems} includeCustomProperties={props.includeCustomProperties as any} showBorrowedProperties={props.showBorrowedProperties} excludeDimensionProperties={props.excludeDimensionProperties} excludeLookupTableProperties={props.excludeLookupTableProperties} excludeClearProperties={props.excludeClearProperties} propertyCombineMode={props.propertyCombineMode as PropertyCombineMode} specialCohorts={props.specialCohorts} otherOptions={otherOptions} selectAllMechanism={props.selectAllMechanism} showSelectAll={props.showSelectAll} selectAllDisabled={props.selectAllDisabled} hideRecentlyViewed={props.hideRecentlyViewed} projectIsFreePlan={props.projectIsFreePlan} selectableSensitiveProperties={props.selectableSensitiveProperties} menuItemTypes={props.menuItemTypes} contextPanelAlignment={props.contextPanelAlignment} typeFilter={props.typeFilter} allDataGroups={props.allDataGroups} dataGroupIds={convertDataGroupIdToSet(props.dataGroupId, props.dataGroupIds)} metricLevelDataGroups={props.metricLevelDataGroups} eventNames={props.eventNames} hideAllPropertiesSelect={props.hideAllPropertiesSelect} hideEditControls={props.hideEditControls} hideEvents={props.hideEvents} hideExperiments={props.hideExperiments} inlineCohortsOverride={props.inlineCohortsOverride} multiple={props.multiple} open={open} projectHasCohorts={props.projectHasCohorts} projectHasPeople={props.projectHasPeople} projectId={props.projectId} readonly={props.readonly} showAllEvents={props.showAllEvents} showAnyEvent={props.showAnyEvent} showSessionEvent={props.showSessionEvent} showSpecialDatasetProperty={props.showSpecialDatasetProperty} showSpecialDatePropertyUnit={props.showSpecialDatePropertyUnit} showTopEvents={props.showTopEvents} supportB2bCohorts={props.supportB2bCohorts} trackName={props.trackName} userId={props.userId} workspaceId={props.workspaceId} onSelect={item => handleSelect({
          detail: item
        })} onDelete={handleDelete} onTooltipContextEditDescription={handleTooltipContextEditDescription} onRender={onChildMenuUpdate} />;
      case MenuType.Filter:
        return <FilterMenuReact key={key} ref={setRef} keydownProxyEl={keydownProxyEl} listProperty={props.listProperty} selected={selected} enableEnhancedListFilters={props.enableEnhancedListFilters} enableWorkspaceFilterRestrictions={props.enableWorkspaceFilterRestrictions} disableValueSelection={props.disableValueSelection} includeCustomProperties={props.includeCustomProperties} showBorrowedProperties={props.showBorrowedProperties} displayContext={props.displayContext as FilterMenuDisplayContext} disabledItems={props.disabledItems} otherFilterOptions={props.otherFilterOptions} allDataGroups={props.allDataGroups} dataGroupIds={convertDataGroupIdToSet(props.dataGroupId, props.dataGroupIds)} metricLevelDataGroups={props.metricLevelDataGroups} enableAllDataGroupsForScdProps={props.enableAllDataGroupsForScdProps} disableAllScdProps={props.disableAllScdProps} eventNames={props.eventNames} excludeDimensionProperties={props.excludeDimensionProperties} excludeLookupTableProperties={props.excludeLookupTableProperties} hideEditControls={props.hideEditControls} hideEvents={props.hideEvents} hideExperiments={props.hideExperiments} inlineCohortsOverride={props.inlineCohortsOverride} open={open} projectHasCohorts={props.projectHasCohorts} projectHasPeople={props.projectHasPeople} projectId={props.projectId} readonly={props.readonly} showSpecialDatasetProperty={props.showSpecialDatasetProperty} supportB2bCohorts={props.supportB2bCohorts} trackName={props.trackName} userId={props.userId} workspaceId={props.workspaceId} onSelect={handleSelect} onRender={onChildMenuUpdate} />;
      case MenuType.Breakdown:
        return <BreakdownMenu key={key} ref={setRef} listProperty={props.listProperty} dataGroupIds={convertDataGroupIdToSet(props.dataGroupId, props.dataGroupIds)} open={open} projectId={props.projectId} selected={props.selected} showSpecialDatePropertyUnit={props.showSpecialDatePropertyUnit} workspaceId={props.workspaceId} includeCustomProperties={props.includeCustomProperties} showBorrowedProperties={props.showBorrowedProperties} allDataGroups={props.allDataGroups} excludeDimensionProperties={props.excludeDimensionProperties} excludeLookupTableProperties={props.excludeLookupTableProperties} hideAllPropertiesSelect={props.hideAllPropertiesSelect} hideEditControls={props.hideEditControls} hideEvents={props.hideEvents} hideExperiments={props.hideExperiments} inlineCohortsOverride={props.inlineCohortsOverride} multiple={props.multiple} projectHasCohorts={props.projectHasCohorts} projectHasPeople={props.projectHasPeople} showAllEvents={props.showAllEvents} showAnyEvent={props.showAnyEvent} showSessionEvent={props.showSessionEvent} showSpecialDatasetProperty={props.showSpecialDatasetProperty} showTopEvents={props.showTopEvents} supportB2bCohorts={props.supportB2bCohorts} eventNames={props.eventNames} readonly={props.readonly} trackName={props.trackName} onSelect={handleSelect} onRender={onChildMenuUpdate} />;
      case MenuType.Formulas:
        return <FormulasMenuReact key={key} ref={setRef} selected={selected} open={open} eventNames={props.eventNames} projectId={props.projectId} readonly={props.readonly} trackName={props.trackName} userId={props.userId} workspaceId={props.workspaceId} keydownProxyEl={keydownProxyEl} onSetKeydownProxyEl={setKeydownProxyEl} onSelect={handleSelect} onCreate={props.onCreate} onRender={onChildMenuUpdate} />;
    }
    return null;
  })()} />;
});
QueryBlock.displayName = "QueryBlock";
const defaultProps: QueryBlockParams = {
  advancedMenuSections: [],
  buttonSize: ButtonSize.Medium,
  dataGroups: [],
  disabledItems: {},
  excludedElements: [],
  includeCustomProperties: IncludeCustomPropertyType.None,
  menuAlignment: Directions.Down,
  menuOffset: 0,
  otherAggregationOptions: [],
  otherFilterOptions: [],
  otherGroupByOptions: [],
  selectAllMechanism: SelectAllMechanism.Full,
  selectorType: SelectorType.Text,
  theme: ButtonThemes.Primary,
  typeFilter: []
};
QueryBlock.defaultProps = defaultProps;
function useChildMenuSync(childMenu: ChildMenuInterface) {
  const {
    print
  } = useTranslation();
  const [, forceRender] = useState({});
  const waitingForRender = useRef(false);
  useEffect(() => {
    waitingForRender.current = false;
  });
  const getChildMenuState = () => {
    var _childMenu$selectedLa2, _childMenu$getPrefixL2, _childMenu$selectedBr2, _childMenu$selectedLe2, _childMenu$selectedRi2;
    return {
      selectedLabel: print(childMenu === null || childMenu === void 0 || (_childMenu$selectedLa2 = childMenu.selectedLabel) === null || _childMenu$selectedLa2 === void 0 ? void 0 : _childMenu$selectedLa2.call(childMenu)),
      getPrefixLabel: print(childMenu === null || childMenu === void 0 || (_childMenu$getPrefixL2 = childMenu.getPrefixLabel) === null || _childMenu$getPrefixL2 === void 0 ? void 0 : _childMenu$getPrefixL2.call(childMenu)),
      selectedBrowserTooltipLabel: print(childMenu === null || childMenu === void 0 || (_childMenu$selectedBr2 = childMenu.selectedBrowserTooltipLabel) === null || _childMenu$selectedBr2 === void 0 ? void 0 : _childMenu$selectedBr2.call(childMenu)),
      selectedLeftIcon: childMenu === null || childMenu === void 0 || (_childMenu$selectedLe2 = childMenu.selectedLeftIcon) === null || _childMenu$selectedLe2 === void 0 ? void 0 : _childMenu$selectedLe2.call(childMenu),
      selectedRightIcon: childMenu === null || childMenu === void 0 || (_childMenu$selectedRi2 = childMenu.selectedRightIcon) === null || _childMenu$selectedRi2 === void 0 ? void 0 : _childMenu$selectedRi2.call(childMenu)
    };
  };
  const lastRenderState = getChildMenuState();
  return () => {
    // since we're depending on child menu state, we need to force a re-render here whenever certain
    // state updates there. this is a hack to help with ui weirdness but the whole relationship needs
    // to be seriously refactored.
    const latestChildMenuState = getChildMenuState();
    if (!isEqual(lastRenderState, latestChildMenuState) && !waitingForRender.current) {
      forceRender({});
      waitingForRender.current = true;
    }
  };
}
export const QueryBlockReact = QueryBlock;